import { useState } from 'react';
import { motion } from 'framer-motion';
import type { AssessmentResult } from '@/types/calculator';
import {
  ShieldCheck, Activity, AlertTriangle, Beaker, Microscope,
  Zap, FlaskConical, CheckCircle2, XCircle, Minus, TrendingUp,
  Thermometer, Droplets, Layers, BarChart3, Info
} from 'lucide-react';

interface Props { result: AssessmentResult; }

const TabButton = ({
  tab, active, onClick, icon: Icon, label, count,
}: {
  tab: string; active: string; onClick: (t: string) => void;
  icon: React.ElementType; label: string; count?: number;
}) => {
  const isActive = active === tab;
  return (
    <button
      onClick={() => onClick(tab)}
      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all border ${
        isActive
          ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400 shadow-md shadow-cyan-500/10'
          : 'bg-slate-800/30 border-transparent text-slate-500 hover:text-slate-300 hover:bg-slate-800/50'
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
      {count !== undefined && count > 0 && (
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${isActive ? 'bg-cyan-500/20 text-cyan-400' : 'bg-slate-700 text-slate-400'}`}>{count}</span>
      )}
    </button>
  );
};

export default function AssessmentResults({ result }: Props) {
  const [tab, setTab] = useState('classification');

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-emerald-400';
    if (score >= 60) return 'text-amber-400';
    return 'text-red-400';
  };
  const getScoreBg = (score: number) => {
    if (score >= 80) return 'bg-emerald-500/10 border-emerald-500/20';
    if (score >= 60) return 'bg-amber-500/10 border-amber-500/20';
    return 'bg-red-500/10 border-red-500/20';
  };
  const getStatusStyle = (status: string) => {
    if (status === 'PASS') return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20';
    if (status === 'FAIL') return 'text-red-400 bg-red-500/10 border-red-500/20';
    return 'text-amber-400 bg-amber-500/10 border-amber-500/20';
  };

  return (
    <div className="space-y-4">
      {/* Tab bar */}
      <div className="flex flex-wrap gap-2">
        <TabButton tab="classification" active={tab} onClick={setTab} icon={ShieldCheck} label="Classification" />
        <TabButton tab="toxicity" active={tab} onClick={setTab} icon={AlertTriangle} label="Toxicity" count={result.toxicParametersCount} />
        <TabButton tab="biological" active={tab} onClick={setTab} icon={Microscope} label="Biological Viability" count={result.biologicalViability.length} />
        <TabButton tab="process" active={tab} onClick={setTab} icon={FlaskConical} label="Process Compatibility" count={result.processCompatibility.length} />
        <TabButton tab="findings" active={tab} onClick={setTab} icon={BarChart3} label="Key Findings" count={result.keyFindings.length} />
      </div>

      {/* ─── CLASSIFICATION TAB ─── */}
      {tab === 'classification' && (
        <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          {/* Treatability Score */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="p-3 rounded-xl bg-slate-800/40 border border-slate-800/50">
              <span className="text-[10px] text-slate-500 uppercase font-bold block mb-1">Treatability Index</span>
              <p className={`text-lg font-bold ${getScoreColor(100 - (result.overallTreatabilityIndex * 10))}`}>{result.overallTreatabilityIndex}</p>
              <span className="text-[10px] text-slate-500">Lower = easier to treat</span>
            </div>
            <div className="p-3 rounded-xl bg-slate-800/40 border border-slate-800/50">
              <span className="text-[10px] text-slate-500 uppercase font-bold block mb-1">Classification</span>
              <p className="text-sm font-bold text-cyan-400">{result.treatabilityClassification}</p>
            </div>
            <div className="p-3 rounded-xl bg-slate-800/40 border border-slate-800/50">
              <span className="text-[10px] text-slate-500 uppercase font-bold block mb-1">Biological Viability</span>
              <p className="text-sm font-bold text-emerald-400">{result.biologicalViabilityScore}</p>
            </div>
            <div className="p-3 rounded-xl bg-slate-800/40 border border-slate-800/50">
              <span className="text-[10px] text-slate-500 uppercase font-bold block mb-1">Recommended Approach</span>
              <p className="text-sm font-bold text-rb-brand">{result.recommendedApproach}</p>
            </div>
          </div>

          {/* Parameter Classification Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left border-b border-slate-700 text-[10px] text-slate-500 uppercase tracking-wider">
                  <th className="pb-2 pr-3">Parameter</th>
                  <th className="pb-2 pr-3">Level</th>
                  <th className="pb-2 pr-3">Score</th>
                  <th className="pb-2 pr-3">Classification</th>
                  <th className="pb-2">Implication</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/50">
                {result.parameterClassification.map((pc, i) => (
                  <tr key={i} className="hover:bg-slate-800/30 transition-colors">
                    <td className="py-2 pr-3 font-bold text-white">{pc.parameter}</td>
                    <td className="py-2 pr-3">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${getStatusStyle(pc.level === 'High' ? 'FAIL' : pc.level === 'Moderate' ? 'PASS' : 'PASS')}`}>{pc.level}</span>
                    </td>
                    <td className="py-2 pr-3 font-mono text-slate-400">{pc.score.toFixed(1)}</td>
                    <td className="py-2 pr-3 text-slate-400">{pc.classification}</td>
                    <td className="py-2 text-[10px] text-slate-500">{pc.implication}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}

      {/* ─── TOXICITY TAB ─── */}
      {tab === 'toxicity' && (
        <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          {result.toxicParametersCount === 0 ? (
            <div className="p-6 text-center rounded-xl bg-emerald-500/5 border border-emerald-500/20">
              <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
              <p className="text-sm font-bold text-emerald-400">No Toxic Compounds Detected</p>
              <p className="text-xs text-slate-500 mt-1">All tested parameters are within safe ranges for biological treatment.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 p-3 rounded-xl bg-red-500/5 border border-red-500/20">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                <div>
                  <p className="text-sm font-bold text-red-400">{result.toxicParametersCount} Toxic Parameter{result.toxicParametersCount !== 1 ? 's' : ''} Detected</p>
                  <p className="text-xs text-slate-400">These compounds can inhibit biological treatment processes.</p>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left border-b border-slate-700 text-[10px] text-slate-500 uppercase tracking-wider">
                      <th className="pb-2 pr-3">Parameter</th>
                      <th className="pb-2 pr-3">Value</th>
                      <th className="pb-2 pr-3">Threshold</th>
                      <th className="pb-2 pr-3">Status</th>
                      <th className="pb-2">Impact</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/50">
                    {result.toxicityAssessment.filter(t => t.status === 'FAIL').map((ta, i) => (
                      <tr key={i} className="hover:bg-slate-800/30 transition-colors">
                        <td className="py-2 pr-3 font-bold text-white">{ta.parameter}</td>
                        <td className="py-2 pr-3 font-mono text-red-400">{typeof ta.value === 'number' ? ta.value.toFixed(3) : String(ta.value)}</td>
                        <td className="py-2 pr-3 font-mono text-slate-400">{typeof ta.threshold === 'number' ? ta.threshold.toFixed(3) : String(ta.threshold)}</td>
                        <td className="py-2 pr-3">
                          <span className="text-[10px] px-1.5 py-0.5 rounded font-bold text-red-400 bg-red-500/10 border border-red-500/20">{ta.status}</span>
                        </td>
                        <td className="py-2 text-slate-400">{ta.impact}</td>
                      </tr>
                    ))}
                    {result.toxicityAssessment.filter(t => t.status !== 'FAIL').map((ta, i) => (
                      <tr key={`p-${i}`} className="hover:bg-slate-800/30 transition-colors">
                        <td className="py-2 pr-3 font-bold text-slate-400">{ta.parameter}</td>
                        <td className="py-2 pr-3 font-mono text-emerald-400">{typeof ta.value === 'number' ? ta.value.toFixed(3) : String(ta.value)}</td>
                        <td className="py-2 pr-3 font-mono text-slate-500">{typeof ta.threshold === 'number' ? ta.threshold.toFixed(3) : String(ta.threshold)}</td>
                        <td className="py-2 pr-3">
                          <span className="text-[10px] px-1.5 py-0.5 rounded font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20">{ta.status}</span>
                        </td>
                        <td className="py-2 text-slate-500">{ta.impact}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </motion.div>
      )}

      {/* ─── BIOLOGICAL VIABILITY TAB ─── */}
      {tab === 'biological' && (
        <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Tests Passed', value: result.biologicalViability.filter(v => v.passFail === 'PASS').length, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
              { label: 'Tests Failed', value: result.biologicalViability.filter(v => v.passFail === 'FAIL').length, color: 'text-red-400', bg: 'bg-red-500/10' },
              { label: 'Total Tests', value: result.biologicalViability.length, color: 'text-white', bg: 'bg-slate-800' },
              { label: 'Viability', value: result.biologicalViability.filter(v => v.passFail === 'PASS').length >= 5 ? 'VIABLE' : 'NOT VIABLE', color: result.biologicalViability.filter(v => v.passFail === 'PASS').length >= 5 ? 'text-emerald-400' : 'text-red-400', bg: result.biologicalViability.filter(v => v.passFail === 'PASS').length >= 5 ? 'bg-emerald-500/10' : 'bg-red-500/10' },
            ].map((stat, i) => (
              <div key={i} className={`p-3 rounded-xl ${stat.bg} border border-slate-800/50`}>
                <span className="text-[10px] text-slate-500 uppercase font-bold block">{stat.label}</span>
                <p className={`text-base font-bold ${stat.color}`}>{stat.value}</p>
              </div>
            ))}
          </div>

          {/* Test details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {result.biologicalViability.map((test, i) => (
              <div key={i} className={`p-3 rounded-xl border transition-all hover:border-slate-600 ${getStatusStyle(test.passFail)}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold">{test.factor}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${getStatusStyle(test.passFail)}`}>{test.passFail}</span>
                </div>
                <div className="flex items-center gap-2 text-[10px] text-slate-400">
                  <span>Value: {typeof test.value === 'number' ? test.value.toLocaleString('en-GB', { maximumFractionDigits: 1 }) : String(test.value)}</span>
                  <span>·</span>
                  <span>{test.criterion}</span>
                </div>
                <p className="text-[10px] text-slate-500 mt-1">{test.assessment}</p>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* ─── PROCESS COMPATIBILITY TAB ─── */}
      {tab === 'process' && (
        <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          {/* Top processes */}
          <div className="space-y-1.5">
            {result.processCompatibility.slice(0, 10).map((proc, i) => (
              <div key={i} className={`flex items-center justify-between p-2.5 rounded-lg border transition-all hover:border-slate-600 ${getScoreBg(proc.score)}`}>
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="text-[10px] text-slate-500 w-5 text-right">#{i + 1}</span>
                  <span className="text-xs font-bold text-white truncate">{proc.process}</span>
                  <span className="text-[10px] text-slate-500">({proc.type})</span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`text-xs font-bold ${getScoreColor(proc.score)}`}>{proc.score}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${proc.recommendation === 'RECOMMENDED' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : proc.recommendation === 'CONDITIONAL' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                    {proc.recommendation}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Treatment train */}
          <div>
            <h4 className="text-[10px] text-slate-500 uppercase font-bold tracking-wider mb-2">Recommended Treatment Train</h4>
            <div className="space-y-2">
              {result.treatmentTrain.map((stage, i) => (
                <div key={i} className="flex items-center gap-3 p-2.5 rounded-xl bg-slate-800/30 border border-slate-800/50">
                  <div className="w-7 h-7 rounded-lg bg-rb-brand/10 border border-rb-brand/20 flex items-center justify-center shrink-0">
                    <span className="text-[10px] font-bold text-rb-brand">{i + 1}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-white">{stage.stage}</p>
                    <p className="text-[10px] text-slate-400 truncate">{stage.process}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[10px] text-slate-500">{stage.scoreOrStatus}</p>
                    <p className="text-[10px] text-slate-600">{stage.purpose}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* ─── KEY FINDINGS TAB ─── */}
      {tab === 'findings' && (
        <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
          {result.keyFindings.map((kf, i) => (
            <div key={i} className="p-3 rounded-xl bg-slate-800/30 border border-slate-800/50 hover:border-slate-600 transition-all">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-white">{kf.metric}</span>
                <span className={`text-xs font-bold ${kf.result.includes('VIABLE') ? 'text-emerald-400' : kf.result.includes('NOT') ? 'text-red-400' : kf.result.includes('YES') ? 'text-amber-400' : 'text-cyan-400'}`}>{kf.result}</span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-[10px] text-slate-500">Rating: <span className="text-slate-400">{kf.rating}</span></span>
                <span className="text-slate-400">·</span>
                <span className="text-[10px] text-slate-500">{kf.implication}</span>
              </div>
            </div>
          ))}
        </motion.div>
      )}
    </div>
  );
}
